import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailFinderAnswer } from '../../../models/TrailFinderAnswer.model'
import { TrailFinderAnswerRelations } from '../../../models/TrailFinderAnswerRelations.model'

import UserService from '../../../services/UserService/UserService'
import { TrailFinderAnswerService } from '../../../services/TrailFinderAnswerService/TrailFinderAnswerService'

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import TrailFinderAnswerForm from '../shared/TrailFinderAnswerForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { TrailFeatureService } from '../../../services/TrailFeatureService/TrailFeatureService'
import { TrailService } from '../../../services/TrailService/TrailService'
import { Difficulties } from '../../../models/Difficulties.model'
import { trailStatuses } from '../../../models/TrailStatuses.model'
import { AppStateContext } from '../../../providers/AppStateContext'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailFinderAnswerSectionIds, trailFinderAnswerSections } from '../shared/TrailFinderAnswerSections'

// renders a list of affected filter values as chips, or '-' when the filter is not set
const FilterChips = (props: { items?: { key: string | number; label: React.ReactNode; href?: string }[] }) => {
  const { items } = props
  if (!items || !items.length) return <>-</>
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {items.map((item) =>
        item.href ? (
          <Chip
            key={item.key}
            size="small"
            variant="outlined"
            label={item.label}
            component={Link}
            href={item.href}
            clickable
          />
        ) : (
          <Chip key={item.key} size="small" variant="outlined" label={item.label} />
        ),
      )}
    </Stack>
  )
}

const TrailFinderAnswerDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFinder])

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailFinderAnswerService = useMemo(() => new TrailFinderAnswerService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail Finder Answer Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading: isLoadingTrailFinderAnswer, data: trailFinderAnswer } = useQuery(
    [
      'TrailFinderAnswerService.get',
      {
        id: Number(id),
        relations: [TrailFinderAnswerRelations.question],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailFinderAnswerService.get({
        id: Number(id),
        relations: [TrailFinderAnswerRelations.question],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail finder answer.')
        console.error(err)
      },
    },
  )

  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingSurfaceTypes, data: surfaceTypes } = useQuery(
    ['TrailService.listSurfaceTypes', AppStateContext.getRegion()],
    () => trailService.listSurfaceTypes(),
    {
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch surface types for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )

  const trailFeatureService = useMemo(() => new TrailFeatureService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingTrailFeatures, data: trailFeaturesResponse } = useQuery(
    ['TrailFeatureService.search', { pageSize: 0 }, AppStateContext.getRegion()],
    () => trailFeatureService.search({ pageSize: 0 }),
    {
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch trail features for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )
  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(!isLoadingTrailFinderAnswer && !isLoadingTrailFeatures && !isLoadingSurfaceTypes ? false : true)
  }, [isLoadingTrailFinderAnswer, isLoadingTrailFeatures, isLoadingSurfaceTypes])

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (trailFinderAnswer?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailFinderAnswerService
        .delete(trailFinderAnswer.id)
        .then(() => {
          SnackbarContext.show(
            `Trail Finder Answer deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the trailFinderAnswer list page
          setTimeout(() => {
            navigate(`/trail-finder-questions/${trailFinderAnswer?.question?.id}`)
            queryClient.removeQueries([
              'TrailFinderAnswerService.get',
              {
                id: Number(id),
                relations: [TrailFinderAnswerRelations.question],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail Finder Answer failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const sectionById = (sectionId: TrailFinderAnswerSectionIds) =>
    trailFinderAnswerSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailFinderAnswerSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined
  const affectedFilters = trailFinderAnswer?.affectedFilters

  return (
    <>
      {trailFinderAnswer && (
        <FormDialog<TrailFinderAnswer>
          open={isEditing}
          entity={trailFinderAnswer}
          entityLabel="Trail Finder Answer"
          onClose={closeEditor}
        >
          <TrailFinderAnswerForm
            trailFinderAnswer={trailFinderAnswer}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this trail finder answer?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {((isLoading && !loadingError) || isLoadingSurfaceTypes || isLoadingTrailFeatures) && (
          <Loading sx={{ py: 20 }} />
        )}
        {loadingError && !isLoadingSurfaceTypes && !isLoadingTrailFeatures && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && !isLoadingSurfaceTypes && !isLoadingTrailFeatures && trailFinderAnswer && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Trail Finder',
                          href: '/trail-finder-questions',
                        },
                      ]
                      if (trailFinderAnswer.question?.id) {
                        breadcrumbs.push({
                          label: `Question ID: ${trailFinderAnswer.question.id}`,
                          href: `/trail-finder-questions/${trailFinderAnswer.question.id}`,
                        })
                      }
                      breadcrumbs.push({ label: `Answer ID: ${trailFinderAnswer.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={trailFinderAnswer.answer}
                  active={trailFinderAnswer.active}
                  subtitle={`${trailFinderAnswer.region} · ID ${trailFinderAnswer.id}`}
                  meta={[
                    `Updated ${formatDate(trailFinderAnswer.updatedAt)}`,
                    `Created ${formatDate(trailFinderAnswer.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailFinderAnswerSectionIds.general)}
                      onEdit={editSectionHandler(TrailFinderAnswerSectionIds.general)}
                    >
                      <DetailField label="Question">
                        {trailFinderAnswer.question ? (
                          <Link href={`/trail-finder-questions/${trailFinderAnswer.question.id}`}>
                            {trailFinderAnswer.question.question}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                      <DetailField label="Answer">{trailFinderAnswer.answer || '-'}</DetailField>
                      <DetailFieldGrid>
                        <DetailField label="Visibility">{trailFinderAnswer.active ? 'Active' : 'Inactive'}</DetailField>
                        <DetailField label="Sort Weight">{trailFinderAnswer.sortWeight ?? '-'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Affected Filters ---------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailFinderAnswerSectionIds.filters)}
                      onEdit={editSectionHandler(TrailFinderAnswerSectionIds.filters)}
                    >
                      {!affectedFilters ? (
                        <Typography variant="body1" color="grey.500">
                          None
                        </Typography>
                      ) : (
                        <DetailFieldGrid>
                          <DetailField label="Difficulties">
                            <FilterChips
                              items={affectedFilters.difficulties?.map((difficulty) => ({
                                key: difficulty,
                                label: Object.entries(Difficulties).find(
                                  (eachDifficulty) => eachDifficulty[1] === difficulty,
                                )?.[0],
                              }))}
                            />
                          </DetailField>
                          <DetailField label="Radius">
                            <FilterChips
                              items={affectedFilters.radius?.map((radius) => ({
                                key: radius,
                                label: `${radius} Miles`,
                              }))}
                            />
                          </DetailField>
                          <DetailField label="Trail Features">
                            <FilterChips
                              items={affectedFilters.features?.map((trailFeatureId) => ({
                                key: trailFeatureId,
                                label:
                                  trailFeaturesResponse?.data.find((feature) => feature.id === trailFeatureId)?.name ??
                                  `Feature ID ${trailFeatureId}`,
                                href: `/trail-features/${trailFeatureId}`,
                              }))}
                            />
                          </DetailField>
                          <DetailField label="Statuses">
                            <FilterChips
                              items={affectedFilters.statuses?.map((status) => ({
                                key: status,
                                label: trailStatuses.find((trailStatus) => trailStatus.id === status)?.name,
                              }))}
                            />
                          </DetailField>
                          <DetailField label="Surface Types">
                            <FilterChips
                              items={affectedFilters.surfaceTypes?.map((surfaceType) => ({
                                key: surfaceType,
                                label: surfaceTypes?.find((type) => type === surfaceType) ?? surfaceType,
                              }))}
                            />
                          </DetailField>
                        </DetailFieldGrid>
                      )}
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailFinderAnswerDetail
